import TrafficLightAlarmActions, { validTrafficLightActions } from '../traffic-lights/TrafficLightAlarmActions.jsx';
import React, {useState, useEffect, useMemo} from 'react';
import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    CircularProgress
} from '@mui/material';
import ObjectConditions from './conditions/ObjectConditions';
import AnalysisConditions from './conditions/AnalysisConditions';
import CountConditions from './conditions/CountConditions';
import TextReasoningConditions from './conditions/TextReasoningConditions';
import AlarmSummary from './AlarmSummary';

const deepClone = (value) => {
    if (!value) {
        return {};
    }

    if (typeof structuredClone === 'function') {
        return structuredClone(value);
    }

    try {
        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        return {...value};
    }
};

const AlarmModal = ({handleClose, handleSave, handleClone, displayInfo, initialAlarm = null, selectedHornId = -1}) => {
    const [objects, setObjects] = useState([]);
    const [analysisData, setAnalysisData] = useState([]);
    const [filteredAnalysisData, setFilteredAnalysisData] = useState([]);

    const [canSave, setCanSave] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingZoneChange, setPendingZoneChange] = useState(null);
    const [notEdited, setNotEdited] = useState(true);

    const memoizedInitialAlarm = useMemo(() => ({
        rule: '',
        zone_id: 0,
        zone: 'Entire Image',
        hasMissingObjects: false,
        objects: [],
        analysis: [],
        threshold: 0,
        min_duration: 0,
        alarm_frequency: 10,
        status: 'inactive',
        notifications_enabled: true,
        text_reasoning: null,
        audio_message: '',
        traffic_light_actions: [],
        ...deepClone(initialAlarm),
    }), [initialAlarm]);

    const [alarm, setAlarm] = useState(memoizedInitialAlarm);
    const [loading, setLoading] = useState(false);
    const trafficLights = displayInfo.find((info) => Number(info.zone.value) === Number(alarm.zone_id))?.zone?.traffic_lights || [];
    const trafficActionsValid = !trafficLights.length || validTrafficLightActions(alarm.traffic_light_actions || [], trafficLights);

    const statuses = [
        {value: "active", label: '🟢 Active'},
        {label: '🔴 Inactive', value: "inactive"}
    ];

    const notificationOptions = [
        {value: true, label: '🔔 Enabled'},
        {value: false, label: '🔕 Muted'}
    ];
    const [zones, setZones] = useState([]);

    const [showAnalyze, setShowAnalyze] = useState(false);

    const isNewAlarm = !initialAlarm;

    const isTextClassOption = (option) => {
        const label = (option?.text || '').toString().trim().toLowerCase();
        const tagName = (option?.tag?.name || '').toString().trim().toLowerCase();

        if (tagName === 'text') {
            return true;
        }

        // Handle legacy labels like "Zona:Text" / "Entire Image: Text".
        return label.endsWith(':text') || label.endsWith(' text') || label === 'text';
    };

    const filterOutTextClassOptions = (items = []) => {
        return items.filter((item) => !isTextClassOption(item));
    };

    // Helper function to determine if text reasoning should be shown
    const shouldShowTextReasoning = () => {
        if (isNewAlarm) {
            return false;
        }

        // Get the current zone's data
        const zoneSelected = alarm.zone_id;
        const displayDataForZone = displayInfo.find((info) => info.zone.value === zoneSelected);
        const hasCaptioning = displayDataForZone?.zone?.caption || false;

        return hasCaptioning && (zoneSelected === 0 || zoneSelected !== 0);
    };

    useEffect(() => {
        setAlarm(memoizedInitialAlarm);
        setNotEdited(true);
        setPendingZoneChange(null);
        setConfirmDialogOpen(false);
    }, [memoizedInitialAlarm]);

    useEffect(() => {
        // go over all the displayIndo and set the zones and objects
        let zones = displayInfo.map((info) => {
            return info.zone;
        });
        setZones((prev) => zones);
        let zoneSelected = alarm.zone_id;
        let objectsSelectedRaw = (displayInfo.find((info) => info.zone.value === zoneSelected) || {data: {objects: []}}).data.objects;
        let objectsSelected = filterOutTextClassOptions(objectsSelectedRaw);
        setObjects((prev) => objectsSelected);
        let zonaData =zones.find((zone,idx) => (zone.value).toString() === zoneSelected.toString());
        let zoneText =  'Entire Image';
        if (zonaData && zoneSelected.toString() !=='0') {
            zoneText = "Zona " +  zoneSelected;
        }
        setAlarm((prevState) =>{return {...prevState, zone: zoneText}});

    }, [displayInfo]);

    const handleChange = (e) => {
        if (alarm.objects.length !== 0 && e.target.name === 'zone_id') {
            setPendingZoneChange(e.target.value);
            setConfirmDialogOpen(true);
        } else {
            if (e.target.name === 'min_duration') {
                setNotEdited(false);
            }
            setAlarm({...alarm, [e.target.name]: e.target.value, ...(e.target.name === 'zone_id' ? { traffic_light_actions: [] } : {})});
        }
    };

    const handleConfirmZoneChange = (confirm) => {
        setConfirmDialogOpen(false);
        if (confirm && pendingZoneChange !== null) {
            // set zone_id and zone
            setAlarm({
                ...alarm,
                zone_id: pendingZoneChange,
                traffic_light_actions: [],
                zone: pendingZoneChange === 0 ? 'Entire Image' : `Zona ${pendingZoneChange}`
            });
            setPendingZoneChange(null);
        }
    };

    const handleAlarmChange = (conditions) => {
        setAlarm((prev) => ({...prev, ...conditions}));
    };

    useEffect(() => {
        setLoading((prev) => true);
        let zoneSelected = alarm.zone_id;
        let displayDataForZone = displayInfo.find((info) => info.zone.value === zoneSelected) || {
            data: {
                objects: [],
                analizedData: []
            }
        };
        let objectsSelected = filterOutTextClassOptions(displayDataForZone.data.objects || []);
        let analizedDataSelected = displayDataForZone.data.analizedData;
        setAnalysisData(analizedDataSelected);
        setObjects(objectsSelected);
        // go over all the objects from alarm and check if they are in the objectsSelected, if not remove them
        let newObjects = alarm.objects;
        console.log(newObjects);
        console.log('objectsSelected', objectsSelected);
        // remove from objects if value is not in objectsSelected
        newObjects = newObjects.filter((obj) => objectsSelected.find((objSel) => objSel.value.toString() === obj.object.value.toString() /* || objSel.tag.id == obj.object?.tag.id */) !== undefined);
        console.log(newObjects);
        // go over the new Objects and fix the value
        newObjects = newObjects.map((obj) => {
            // update the value based on the tag.value in availableObjects value
            let index = objectsSelected.findIndex((available) => available.value.toString() === obj?.object?.value.toString() /*|| available?.tag.id == obj?.object?.tag.id*/);
            return {
                ...obj,
                value: index
            };
        });
        console.log('mapped', newObjects);
        setAlarm((prev) => ({...prev, objects: newObjects}));

        // go over all the analysis from alarm and check if they are in the analizedDataSelected, if not remove them
        let newAnalysis = alarm.analysis;
        // remove from objects if value is not in objectsSelected
        newAnalysis = newAnalysis.filter((analysis) => analysis.analysisData && analizedDataSelected.some((analysisSel) =>
            analysisSel.class_id == analysis.analysisData.class_id &&
            analysisSel.model == analysis.analysisData.model &&
            analysisSel.name == analysis.analysisData.name));
        setAlarm((prev) => ({...prev, analysis: newAnalysis}));

        setTimeout(() => {
            setLoading((prev) => false);
        }, 500);
    }, [alarm.zone_id]);

    useEffect(() => {
        if (analysisData.length == 0) {
            setShowAnalyze(false);
            return;
        }
        console.log('analysisData', analysisData);
        // filter Analysis data to get only the ones that have class_id = alarm.objects[].object.tag.id
        let filteredAnalysisData = analysisData;
        if (alarm.objects.length > 0) {
            filteredAnalysisData = analysisData.filter((analysis) => {
                const analysisClassId = analysis?.class_id?.toString();
                return alarm.objects.some((objSel) => {
                    const baseClassId = objSel?.object?.tag?.id?.toString();
                    const objectClasses = (objSel?.object?.classes || []).map((id) => id.toString());
                    const detectorChildClasses = (objSel?.object?.tags || [])
                        .map((tag) => tag?.id?.toString())
                        .filter(Boolean);

                    return baseClassId === analysisClassId
                        || objectClasses.includes(analysisClassId)
                        || detectorChildClasses.includes(analysisClassId);
                });
            });
        }
        setFilteredAnalysisData(filteredAnalysisData);
        setShowAnalyze(filteredAnalysisData.length > 0);
        // if alarm.analysis is not in the filteredAnalysisData remove it
        let newAnalysis = alarm.analysis;
        console.log('newAnalysis', newAnalysis);
        newAnalysis = newAnalysis.filter((analysis) => analysis.analysisData && filteredAnalysisData.some((analysisSel) => analysisSel.class_id == analysis.analysisData.class_id));
        console.log('newAnalysis', newAnalysis);
        setAlarm((prev) => ({...prev, analysis: newAnalysis}));

    }, [alarm.objects, analysisData]);
    const renderConditionComponent = () => {
        const hasObjects = alarm.objects && Object.keys(alarm.objects).length > 0;
        if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}><CircularProgress/></Box>;


        return (
            <>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <TextField
                        margin="dense"
                        select
                        label="Notifications"
                        name="notifications_enabled"
                        fullWidth
                        onChange={handleChange}
                        value={alarm.notifications_enabled !== false}
                        sx={{ flex: 1 }}
                    >
                        {notificationOptions.map((option) => (
                            <MenuItem key={String(option.value)} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <ObjectConditions
                    value={alarm}
                    update={loading}
                    onChange={handleAlarmChange}
                    availableObjects={objects}
                />

                {!loading && (showAnalyze || analysisData.length > 0) && (<AnalysisConditions
                    value={alarm}
                    onChange={handleAlarmChange}
                    analysisData={showAnalyze ? filteredAnalysisData : analysisData}
                />)
                }
                {!loading && (<CountConditions value={alarm} onChange={handleAlarmChange}/>)}
                {shouldShowTextReasoning() && (
                    <TextReasoningConditions
                        value={alarm}
                        onChange={handleAlarmChange}
                    />
                )}
                {selectedHornId >= 0 && (
                    <>
                        <Alert severity="warning" sx={{mt: 2}}>
                            Please write the message in Romanian using diacritics.
                        </Alert>
                        <TextField
                            margin="dense"
                            label="Audio message"
                            name="audio_message"
                            fullWidth
                            multiline
                            minRows={3}
                            onChange={handleChange}
                            value={alarm.audio_message || ''}
                            inputProps={{maxLength: 2000}}
                            helperText={`${(alarm.audio_message || '').length}/2000`}
                        />
                    </>
                )}
            </>
        );
    };

    useEffect(() => {
        let canBeSaved = true;
        if (!alarm.rule || !alarm.status) {
            canBeSaved = false;
        } else if (alarm.objects.length === 0 && (!alarm.analysis || alarm.analysis.length === 0)) {
            canBeSaved = false;
        }

        setCanSave(canBeSaved);
    }, [alarm]);
    useEffect(() => {
        if (!notEdited)
            return;
        // if theshold< 5 set the theshold to 5
        if (alarm.min_duration == 0 && notEdited && alarm.hasMissingObjects) {
            setAlarm({...alarm, min_duration: 5});
        } else if (!alarm.hasMissingObjects && notEdited && alarm.min_duration == 5) {
            setAlarm({...alarm, min_duration: 0});
        }
    }, [alarm.hasMissingObjects]);

    return (
        <>
            <DialogTitle>{initialAlarm ? 'Edit Alarm' : 'Add New Alarm'}</DialogTitle>
            <DialogContent>
                <Box sx={{mt: 2}}>
                    <TextField margin="dense" label="Title" name="rule" fullWidth onChange={handleChange}
                               value={alarm.rule}/>
                    <Box sx={{display: 'flex', gap: 2}}>
                        <TextField margin="dense" select label="Zone" name="zone_id" fullWidth onChange={handleChange}
                                   value={alarm.zone_id} sx={{flex: 1}}>
                            {zones.map((zone) => (
                                <MenuItem key={zone.value} value={zone.value}>{zone.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField margin="dense" select label="Status" name="status" fullWidth onChange={handleChange}
                                   value={alarm.status} sx={{flex: 1}}>
                            {statuses.map((status) => (
                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    {renderConditionComponent()}
                    <TextField
                        margin="dense"
                        type="number"
                        label="Minimum scenario duration (seconds)"
                        name="min_duration"
                        fullWidth
                        onChange={handleChange}
                        value={alarm.min_duration}
                        inputProps={{min: alarm.hasMissingObjects ? 5 : 0}}
                    />
                    <TextField
                        margin="dense"
                        type="number"
                        label="Alarm frequency (seconds)"
                        name="alarm_frequency"
                        fullWidth
                        onChange={handleChange}
                        value={alarm.alarm_frequency}
                        inputProps={{min: 0}}
                    />
                    <TrafficLightAlarmActions actions={alarm.traffic_light_actions || []} lights={trafficLights}
                        onChange={(actions) => setAlarm((current) => ({ ...current, traffic_light_actions: actions }))} />
                    <AlarmSummary alarm={alarm} availableObjects={objects} trafficLights={trafficLights}/>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={() => handleSave({ ...alarm, traffic_light_actions: trafficLights.length ? alarm.traffic_light_actions : [] })}
                    disabled={!canSave || !trafficActionsValid}
                >
                    {initialAlarm ? 'Save Changes' : 'Save Alarm'}
                </Button>
                {initialAlarm && typeof handleClone === 'function' && (
                    <Button
                        variant="outlined"
                        onClick={() => handleClone({ ...alarm, traffic_light_actions: trafficLights.length ? alarm.traffic_light_actions : [] })}
                        disabled={!canSave || !trafficActionsValid}
                    >
                        Clone Alarm
                    </Button>
                )}
            </DialogActions>
            <Dialog open={confirmDialogOpen} onClose={() => handleConfirmZoneChange(false)}>
                <DialogTitle>Confirm Zone Change</DialogTitle>
                <DialogContent>
                    Changing the zone will update the rest of the alarm settings. Do you want to proceed?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleConfirmZoneChange(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleConfirmZoneChange(true)}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AlarmModal;
